import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { SEOHead } from '@/components/SEOHead';
import { SeoIntroFaq } from '@/components/seo/SeoIntroFaq';
import { Layout } from '@/components/layout/Layout';
import { ProjectAdsSidebar } from '@/components/property/ProjectAdsSidebar';
import { NeighborhoodFilterSelect } from '@/components/filters/NeighborhoodFilterSelect';
import { ProjectListingCard } from '@/components/project/ProjectListingCard';
import { TierBadge } from '@/components/property/TierBadge';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Skeleton } from '@/components/ui/skeleton';
import {
  MapPin, Building2, TrendingUp, Search, ChevronLeft, ChevronRight, ChevronDown, X,
  Phone, LayoutGrid, List, Home, ChevronRight as ChevronRightIcon, Map as MapIcon
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useCurrency } from '@/contexts/CurrencyContext';
import { formatPriceRange, formatPriceCompact } from '@/lib/formatPrice';
import { ListingsMapView, MapItem } from '@/components/listings/ListingsMapView';
import { ProjectMapCard } from '@/components/listings/ProjectMapCard';

const FALLBACK_CITIES = ['Addis Ababa', 'Adama', 'Hawassa', 'Bahir Dar', 'Mekelle', 'Dire Dawa', 'Jimma', 'Gondar'];
const STATUS_OPTIONS = [
  { value: 'upcoming', label: 'Off-Plan' },
  { value: 'under_construction', label: 'Under Construction' },
  { value: 'ready', label: 'Ready' },
];

export default function Projects() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || 'en';
  const initialParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams();
  const [searchQuery, setSearchQuery] = useState(initialParams.get('q') || '');
  const [cityFilter, setCityFilter] = useState<string | undefined>(initialParams.get('city') || undefined);
  const [neighborhoodFilter, setNeighborhoodFilter] = useState<string | undefined>(initialParams.get('neighborhood') || initialParams.get('sub_city') || undefined);
  const [statusFilter, setStatusFilter] = useState<string | undefined>(initialParams.get('status') || undefined);
  const [minPrice, setMinPrice] = useState<string | undefined>(initialParams.get('minPrice') || undefined);
  const [maxPrice, setMaxPrice] = useState<string | undefined>(initialParams.get('maxPrice') || undefined);
  const [projectTypeFilter, setProjectTypeFilter] = useState<string | undefined>(initialParams.get('project_type') || undefined);
  const [developerFilter, setDeveloperFilter] = useState<string | undefined>(initialParams.get('developer') || undefined);
  const [minAreaFilter, setMinAreaFilter] = useState<string | undefined>(initialParams.get('minArea') || undefined);
  const [maxAreaFilter, setMaxAreaFilter] = useState<string | undefined>(initialParams.get('maxArea') || undefined);
  const paymentTypesParam = initialParams.get('payment_types');
  const [paymentTypesFilter, setPaymentTypesFilter] = useState<string[]>(paymentTypesParam ? paymentTypesParam.split(',') : []);
  const [sortBy, setSortBy] = useState(initialParams.get('sort_by') || 'newest');
  const [layout, setLayout] = useState<'grid' | 'list' | 'map'>(
    initialParams.get('view') === 'map' ? 'map' : 'list',
  );
  const [page, setPage] = useState(1);
  const trendingRef = useRef<HTMLDivElement>(null);
  const devRef = useRef<HTMLDivElement>(null);

  const { data: projects, isLoading } = useQuery({
    queryKey: ['projects'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('projects')
        .select('*, project_images(*), project_unit_types(*), developer_profiles(company_name, logo_url, city, phone), agencies:marketing_agency_id(id, name, logo_url, slug)')
        .eq('approval_status', 'approved')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data || [];
    },
  });

  const { data: developers } = useQuery({
    queryKey: ['featured-developers'],
    queryFn: async () => {
      const { data, error } = await supabase.from('developer_profiles').select('*').order('total_projects', { ascending: false }).limit(8);
      if (error) throw error;
      return data || [];
    },
  });
  const { data: citiesData } = useQuery({
    queryKey: ['projects-cities'],
    queryFn: async () => {
      const { data } = await supabase.from('cities').select('name').order('name');
      return data?.map((c: any) => c.name) || [];
    },
  });
  const CITIES = (citiesData && citiesData.length > 0) ? citiesData : FALLBACK_CITIES;

  const getName = (val: any) => typeof val === 'object' ? (val?.[lang] || val?.en || '') : (val || '');
  const { displayCurrency, rate } = useCurrency();
  const formatPrice = (min: number | null, max: number | null, currency: string) => {
    if (!min && !max) return 'Price TBD';
    return formatPriceRange(min, max, currency, displayCurrency, rate);
  };
  const scroll = (ref: React.RefObject<HTMLDivElement>, dir: 'left' | 'right') => ref.current?.scrollBy({ left: dir === 'left' ? -320 : 320, behavior: 'smooth' });

  const featuredProjects = projects?.filter((p: any) => p.is_featured) || [];

  const filtered = (projects || []).filter((p: any) => {
    const name = getName(p.name).toLowerCase();
    const dev = p.developer_profiles?.company_name?.toLowerCase() || '';
    const q = searchQuery.toLowerCase();
    if (q && !name.includes(q) && !dev.includes(q) && !p.city?.toLowerCase().includes(q)) return false;
    if (cityFilter && p.city !== cityFilter) return false;
    if (neighborhoodFilter && (p.sub_city || '').toLowerCase() !== neighborhoodFilter.toLowerCase()) return false;
    if (statusFilter && p.status !== statusFilter) return false;
    // Price overlap: include project if its [price_min, price_max] overlaps with user [minPrice, maxPrice]
    const userMin = minPrice ? Number(minPrice) : 0;
    const userMax = maxPrice ? Number(maxPrice) : Infinity;
    const pMin = p.price_min ?? p.price_max ?? 0;
    const pMax = p.price_max ?? p.price_min ?? Infinity;
    if ((minPrice || maxPrice) && !(pMax >= userMin && pMin <= userMax)) return false;
    if (developerFilter && !dev.includes(developerFilter.toLowerCase())) return false;
    if (projectTypeFilter && projectTypeFilter !== 'all') {
      const unitTypes = p.project_unit_types || [];
      if (unitTypes.length === 0) return false;
      const tokens = projectTypeFilter.toLowerCase().split('_').filter(Boolean);
      const matches = unitTypes.some((u: any) => {
        const unitName = (u.name || '').toLowerCase();
        // Studio: match by bedrooms=0 OR name containing "studio"
        if (projectTypeFilter === 'studio') return u.bedrooms === 0 || unitName.includes('studio');
        // Commercial Spaces: match by name containing "commercial"
        if (projectTypeFilter === 'commercial_spaces') return unitName.includes('commercial');
        // Otherwise, every token from the filter must appear in the unit name
        return tokens.every((t: string) => unitName.includes(t));
      });
      if (!matches) return false;
    }
    if (minAreaFilter || maxAreaFilter) {
      const unitTypes = p.project_unit_types || [];
      if (unitTypes.length === 0) return false;
      const hasMatch = unitTypes.some((u: any) => {
        if (u.area_min == null && u.area_max == null) return false;
        const unitMin = u.area_min ?? 0;
        const unitMax = u.area_max ?? Infinity;
        const filterMin = minAreaFilter ? Number(minAreaFilter) : 0;
        const filterMax = maxAreaFilter ? Number(maxAreaFilter) : Infinity;
        return unitMax >= filterMin && unitMin <= filterMax;
      });
      if (!hasMatch) return false;
    }
    if (paymentTypesFilter.length > 0) {
      const types: string[] = Array.isArray(p.payment_types) ? p.payment_types : [];
      if (!paymentTypesFilter.some(t => types.includes(t))) return false;
    }
    return true;
  }).sort((a: any, b: any) => {
    if (sortBy === 'price_asc') return (a.price_min || 0) - (b.price_min || 0);
    if (sortBy === 'price_desc') return (b.price_max || 0) - (a.price_max || 0);
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  });

  const activeFilterCount = [cityFilter, neighborhoodFilter, statusFilter, minPrice || maxPrice, projectTypeFilter && projectTypeFilter !== 'all' ? projectTypeFilter : undefined, developerFilter, minAreaFilter || maxAreaFilter, paymentTypesFilter.length ? 'payment' : undefined].filter(Boolean).length;
  const hasActiveFilters = activeFilterCount > 0 || !!searchQuery;

  const PAGE_SIZE = layout === 'list' ? 10 : 12;
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paginated = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  // Reset page on filter / sort / layout changes
  const resetPage = () => setPage(1);

  const clearAll = () => {
    setCityFilter(undefined); setNeighborhoodFilter(undefined); setStatusFilter(undefined);
    setMinPrice(undefined); setMaxPrice(undefined); setSearchQuery('');
    setProjectTypeFilter(undefined); setDeveloperFilter(undefined);
    setMinAreaFilter(undefined); setMaxAreaFilter(undefined);
    setPaymentTypesFilter([]);
    resetPage();
  };

  const pageNumbers = (): (number | '...')[] => {
    const items: (number | '...')[] = [];
    const add = (n: number | '...') => items.push(n);
    if (totalPages <= 7) { for (let i = 1; i <= totalPages; i++) add(i); return items; }
    add(1);
    if (currentPage > 4) add('...');
    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);
    for (let i = start; i <= end; i++) add(i);
    if (currentPage < totalPages - 3) add('...');
    add(totalPages);
    return items;
  };
  useEffect(() => { setPage(1); }, [searchQuery, cityFilter, neighborhoodFilter, statusFilter, minPrice, maxPrice, sortBy, layout, projectTypeFilter, developerFilter, minAreaFilter, maxAreaFilter, paymentTypesFilter]);

  return (
    <Layout>
      <SEOHead
        title="New Real Estate Projects in Ethiopia — Off-Plan Investments"
        description="Discover new real estate development projects across Ethiopia. Browse off-plan apartments, condos and residential projects in Addis Ababa and other cities."
        canonical="/projects"
      />
      <div className="border-b bg-muted/30">
        <div className="container py-4">
          <nav className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
            <Link to="/" className="hover:text-foreground transition-colors">Home</Link>
            <ChevronRightIcon className="h-3 w-3" />
            <span className="text-foreground font-medium">{t('nav.projects')}</span>
          </nav>
          <div className="flex items-center justify-between">
            <h1 className="font-display text-xl md:text-2xl font-bold">{t('nav.projects')}</h1>
            <p className="text-sm text-muted-foreground">
              <span className="font-semibold text-foreground">{filtered.length}</span> projects found
            </p>
          </div>
        </div>
      </div>

      {/* Sticky Filter Bar */}
      <div className="sticky top-[56px] md:top-[64px] lg:top-[117px] z-30 bg-background border-b shadow-sm">
        <div className="container">
          <div className="flex items-center gap-2 py-3 overflow-x-auto scrollbar-hide">
            <div className="relative shrink-0">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input placeholder="Search projects..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="h-9 w-52 pl-8 text-xs" />
            </div>

            <Select value={cityFilter || 'all'} onValueChange={v => { setCityFilter(v === 'all' ? undefined : v); setNeighborhoodFilter(undefined); }}>
              <SelectTrigger className="h-9 w-auto min-w-[130px] text-xs shrink-0 gap-1.5">
                <Home className="h-3.5 w-3.5 text-muted-foreground" /><SelectValue placeholder="All Cities" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Cities</SelectItem>
                {CITIES.map(c => <SelectItem key={c} value={c} className="text-xs">{c}</SelectItem>)}
              </SelectContent>
            </Select>

            <NeighborhoodFilterSelect
              cityName={cityFilter}
              value={neighborhoodFilter}
              onChange={setNeighborhoodFilter}
            />


            <Select value={statusFilter || 'all'} onValueChange={v => setStatusFilter(v === 'all' ? undefined : v)}>
              <SelectTrigger className="h-9 w-auto min-w-[140px] text-xs shrink-0 gap-1.5">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value} className="text-xs">{s.label}</SelectItem>)}
              </SelectContent>
            </Select>

            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="h-9 text-xs shrink-0 gap-1.5">
                  {minPrice || maxPrice ? `${minPrice ? Number(minPrice).toLocaleString('en-US') : '0'} - ${maxPrice ? Number(maxPrice).toLocaleString('en-US') : '∞'} ETB` : 'Price Range'}
                  <ChevronDown className="h-3 w-3" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-64 p-3" align="start">
                <p className="text-xs font-semibold mb-2">Price Range (ETB)</p>
                <div className="grid grid-cols-2 gap-2">
                  <Input type="number" placeholder="Min" className="h-9 text-xs" value={minPrice || ''} onChange={e => setMinPrice(e.target.value || undefined)} />
                  <Input type="number" placeholder="Max" className="h-9 text-xs" value={maxPrice || ''} onChange={e => setMaxPrice(e.target.value || undefined)} />
                </div>
                <div className="flex gap-1.5 mt-2">
                  {[{ label: '<3M', min: undefined, max: '3000000' }, { label: '3-10M', min: '3000000', max: '10000000' }, { label: '10-30M', min: '10000000', max: '30000000' }, { label: '30M+', min: '30000000', max: undefined }].map(p => (
                    <Button key={p.label} variant="outline" size="sm" className="h-7 text-[10px] flex-1" onClick={() => { setMinPrice(p.min); setMaxPrice(p.max); }}>{p.label}</Button>
                  ))}
                </div>
              </PopoverContent>
            </Popover>

            <div className="h-6 w-px bg-border shrink-0 mx-1" />

            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="h-9 w-auto min-w-[130px] text-xs shrink-0"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">Newest First</SelectItem>
                <SelectItem value="price_asc">Price: Low to High</SelectItem>
                <SelectItem value="price_desc">Price: High to Low</SelectItem>
              </SelectContent>
            </Select>

            <div className="hidden md:flex border rounded-lg overflow-hidden shrink-0 ml-auto">
              <Button variant={layout === 'grid' ? 'secondary' : 'ghost'} size="icon" className="h-9 w-9 rounded-none" onClick={() => setLayout('grid')} title="Grid"><LayoutGrid className="h-4 w-4" /></Button>
              <Button variant={layout === 'list' ? 'secondary' : 'ghost'} size="icon" className="h-9 w-9 rounded-none" onClick={() => setLayout('list')} title="List"><List className="h-4 w-4" /></Button>
              <Button variant={layout === 'map' ? 'secondary' : 'ghost'} size="icon" className="h-9 w-9 rounded-none" onClick={() => setLayout('map')} title="Map"><MapIcon className="h-4 w-4" /></Button>
            </div>
          </div>

          {activeFilterCount > 0 && (
            <div className="flex items-center gap-2 pb-3 overflow-x-auto scrollbar-hide">
              <span className="text-[10px] text-muted-foreground shrink-0 uppercase tracking-wider font-medium">Active:</span>
              {cityFilter && <Badge variant="secondary" className="text-[10px] gap-1 shrink-0">{cityFilter}<X className="h-2.5 w-2.5 cursor-pointer" onClick={() => setCityFilter(undefined)} /></Badge>}
              {statusFilter && <Badge variant="secondary" className="text-[10px] gap-1 capitalize shrink-0">{statusFilter.replace('_', ' ')}<X className="h-2.5 w-2.5 cursor-pointer" onClick={() => setStatusFilter(undefined)} /></Badge>}
              {(minPrice || maxPrice) && <Badge variant="secondary" className="text-[10px] gap-1 shrink-0">{minPrice ? Number(minPrice).toLocaleString('en-US') : '0'} - {maxPrice ? Number(maxPrice).toLocaleString('en-US') : '∞'} ETB<X className="h-2.5 w-2.5 cursor-pointer" onClick={() => { setMinPrice(undefined); setMaxPrice(undefined); }} /></Badge>}
              {projectTypeFilter && projectTypeFilter !== 'all' && <Badge variant="secondary" className="text-[10px] gap-1 capitalize shrink-0">{projectTypeFilter.replace(/_/g, ' ')}<X className="h-2.5 w-2.5 cursor-pointer" onClick={() => setProjectTypeFilter(undefined)} /></Badge>}
              {developerFilter && <Badge variant="secondary" className="text-[10px] gap-1 shrink-0">{developerFilter}<X className="h-2.5 w-2.5 cursor-pointer" onClick={() => setDeveloperFilter(undefined)} /></Badge>}
              {(minAreaFilter || maxAreaFilter) && <Badge variant="secondary" className="text-[10px] gap-1 shrink-0">{minAreaFilter ? Number(minAreaFilter).toLocaleString('en-US') : '0'} - {maxAreaFilter ? Number(maxAreaFilter).toLocaleString('en-US') : '∞'} m²<X className="h-2.5 w-2.5 cursor-pointer" onClick={() => { setMinAreaFilter(undefined); setMaxAreaFilter(undefined); }} /></Badge>}
              {paymentTypesFilter.length > 0 && <Badge variant="secondary" className="text-[10px] gap-1 shrink-0">{paymentTypesFilter.join(', ')}<X className="h-2.5 w-2.5 cursor-pointer" onClick={() => setPaymentTypesFilter([])} /></Badge>}
              <Button variant="ghost" size="sm" className="h-6 text-[10px] text-destructive shrink-0" onClick={clearAll}>Clear All</Button>
            </div>
          )}
        </div>
      </div>

      {/* Featured Projects Carousel */}
      {!hasActiveFilters && featuredProjects.length > 0 && (
        <section className="container py-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-display text-xl font-bold">Discover New Projects</h2>
            <div className="flex gap-2">
              <button onClick={() => scroll(trendingRef, 'left')} className="h-9 w-9 rounded-full border bg-card flex items-center justify-center hover:bg-muted transition-colors"><ChevronLeft className="h-4 w-4" /></button>
              <button onClick={() => scroll(trendingRef, 'right')} className="h-9 w-9 rounded-full border bg-card flex items-center justify-center hover:bg-muted transition-colors"><ChevronRight className="h-4 w-4" /></button>
            </div>
          </div>
          <div ref={trendingRef} className="flex gap-4 overflow-x-auto pb-2" style={{ scrollbarWidth: 'none' }}>
            {featuredProjects.map((project: any) => {
              const name = getName(project.name);
              const img = project.project_images?.find((i: any) => i.is_primary)?.url || project.project_images?.[0]?.url || 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=600&h=400&fit=crop';
              return (
                <Link key={project.id} to={`/projects/${(project as any).slug || project.id}`} className="flex-shrink-0 w-[300px] md:w-[340px] rounded-xl overflow-hidden group relative">
                  <div className="relative aspect-[4/3]">
                    <img src={img} alt={name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                    <span className="absolute top-3 left-3">
                      <TierBadge
                        tier={(project.featured_tier as any) || 'standard'}
                        context="project"
                      />
                    </span>
                    <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                      <h3 className="font-bold text-lg leading-tight mb-1">{name}</h3>
                      <div className="flex items-center gap-1 text-white/80 text-sm"><MapPin className="h-3.5 w-3.5" />{project.city}{project.sub_city ? `, ${project.sub_city}` : ''}</div>
                    </div>
                  </div>
                  <div className="bg-card border border-t-0 rounded-b-xl p-3 space-y-1">
                    <div className="text-xs text-muted-foreground capitalize">{project.status?.replace('_', ' ')}</div>
                    <Badge variant="outline" className="text-primary border-primary font-semibold text-xs">{formatPrice(project.price_min, project.price_max, project.currency || 'ETB')}</Badge>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {/* All Projects: Map or Grid + Sidebar */}
      {layout === 'map' ? (
        <section className="container py-4">
          <ListingsMapView
            items={filtered.map((p: any): MapItem => ({
              id: p.id,
              lat: p.latitude,
              lng: p.longitude,
              title: getName(p.name) || 'Project',
              subtitle: `${p.city || ''}${p.sub_city ? `, ${p.sub_city}` : ''}`,
              priceLabel: p.price_min || p.price_max
                ? `From ${formatPriceCompact(p.price_min || p.price_max, p.currency || 'ETB', displayCurrency, rate)}`
                : 'Price TBD',
              image: p.project_images?.find((i: any) => i.is_primary)?.url || p.project_images?.[0]?.url,
              link: `/projects/${p.slug || p.id}`,
            }))}
            isLoading={isLoading}
            markerVariant="pin-label"
            emptyText="No projects found"
            renderCard={(id, { isActive, onMouseEnter, onMouseLeave }) => {
              const project = filtered.find((p: any) => p.id === id);
              if (!project) return null;
              return (
                <div
                  key={id}
                  onMouseEnter={onMouseEnter}
                  onMouseLeave={onMouseLeave}
                  className={isActive ? 'ring-2 ring-primary rounded-xl' : ''}
                >
                  <ProjectMapCard project={project} lang={lang} />
                </div>
              );
            }}
          />

        </section>
      ) : (
      <section className="container py-6">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-8">
          <div>
            {isLoading ? (
              <div className={`grid gap-4 ${layout === 'grid' ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1'}`}>
                {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className={layout === 'list' ? 'h-56 rounded-xl' : 'h-80 rounded-xl'} />)}
              </div>
            ) : filtered.length > 0 ? (
              <>
                <div className={`grid gap-4 ${layout === 'grid' ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1'}`}>
                  {paginated.map((project: any) => (
                    <ProjectListingCard key={project.id} project={project} layout={layout as 'grid' | 'list'} />
                  ))}
                </div>
                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-1.5 mt-8 flex-wrap">
                    <Button variant="outline" size="icon" className="h-8 w-8" disabled={currentPage === 1}
                      onClick={() => { setPage(p => Math.max(1, p - 1)); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    {pageNumbers().map((n, i) => n === '...' ? (
                      <span key={`e${i}`} className="px-2 text-sm text-muted-foreground">…</span>
                    ) : (
                      <Button key={n} variant={n === currentPage ? 'default' : 'outline'} size="sm"
                        className="h-8 min-w-8 px-2 text-xs"
                        onClick={() => { setPage(n as number); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
                        {n}
                      </Button>
                    ))}
                    <Button variant="outline" size="icon" className="h-8 w-8" disabled={currentPage === totalPages}
                      onClick={() => { setPage(p => Math.min(totalPages, p + 1)); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-20">
                <Building2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground text-lg">No projects found</p>
              </div>
            )}
          </div>
          <aside className="hidden lg:block">
            <div className="sticky top-[140px] lg:top-[200px]">
              <ProjectAdsSidebar page="projects" />
            </div>
          </aside>
        </div>
      </section>
      )}



      {/* Featured Developers */}
      {!hasActiveFilters && developers && developers.length > 0 && (
        <section className="bg-muted/40 py-10">
          <div className="container">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-xl font-bold">Featured Developers</h2>
              <div className="flex gap-2">
                <button onClick={() => scroll(devRef, 'left')} aria-label="Scroll developers left" className="h-8 w-8 rounded-full border bg-card flex items-center justify-center hover:bg-muted transition-colors"><ChevronLeft className="h-4 w-4" /></button>
                <button onClick={() => scroll(devRef, 'right')} aria-label="Scroll developers right" className="h-8 w-8 rounded-full border bg-card flex items-center justify-center hover:bg-muted transition-colors"><ChevronRight className="h-4 w-4" /></button>
              </div>
            </div>
            <div ref={devRef} className="flex gap-3 overflow-x-auto pb-2" style={{ scrollbarWidth: 'none' }}>
              {developers.map((dev: any) => (
                <div key={dev.id} className="flex-shrink-0 w-[260px] flex items-center gap-3 p-4 rounded-xl border bg-card hover:shadow-md transition-shadow">
                  <div className="h-14 w-14 rounded-lg bg-muted flex items-center justify-center shrink-0 overflow-hidden border">
                    {dev.logo_url ? <img src={dev.logo_url} alt={dev.company_name} className="h-full w-full object-cover" /> : <Building2 className="h-6 w-6 text-muted-foreground" />}
                  </div>
                  <div className="min-w-0">
                    <div className="font-semibold text-sm truncate">{dev.company_name}</div>
                    {dev.city && <div className="flex items-center gap-1 text-xs text-primary mt-0.5"><MapPin className="h-3 w-3" /> {dev.city}</div>}
                    {dev.phone && <div className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5"><Phone className="h-3 w-3" /> {dev.phone}</div>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
      <SeoIntroFaq
        intro={
          <>
            <h2 className="text-lg md:text-xl font-bold mb-2">New real estate projects in Ethiopia</h2>
            <p>
              Browse <strong>{filtered.length} active new development projects</strong> from Ethiopia's leading
              developers, including off-plan apartments, condominiums, gated communities and mixed-use towers
              across Addis Ababa, Adama, Hawassa, Bahir Dar and Dire Dawa. Each listing on Ethio Real Estates
              includes verified developer details, floor and payment plans, unit-type breakdowns and live
              construction progress so you can compare projects side-by-side before booking a unit.
            </p>
            <p>
              Use the filters above to narrow by city, neighborhood, budget range in ETB, area in square
              meters, payment option (cash, installments or bank financing) and project status — Off-Plan,
              Under Construction or Ready to Move. Whether you are a first-time buyer, a returning diaspora
              investor, or scouting a yielding rental, our directory is updated daily directly from developers.
            </p>
          </>
        }
        internalLinksTitle="Popular cities for new projects"
        internalLinks={[
          { label: 'Addis Ababa', href: '/projects?city=Addis%20Ababa' },
          { label: 'Adama', href: '/projects?city=Adama' },
          { label: 'Hawassa', href: '/projects?city=Hawassa' },
          { label: 'Bahir Dar', href: '/projects?city=Bahir%20Dar' },
          { label: 'Dire Dawa', href: '/projects?city=Dire%20Dawa' },
          { label: 'Area guides', href: '/area-guides' },
        ]}
        faqs={[
          { q: 'What does Off-Plan mean in Ethiopia?', a: 'Off-Plan projects are sold before construction is completed. Buyers typically pay in scheduled installments tied to construction milestones, often at a lower price than ready units.' },
          { q: 'Can I pay for a new project in installments?', a: 'Most developers on Ethio Real Estates offer installment plans of 12–60 months. Some projects also accept bank financing or full cash payment with a discount.' },
          { q: 'Are projects verified before being listed?', a: 'Yes. Every developer is reviewed by our team and must submit registration documents before they can publish projects. We verify project licenses and locations where available.' },
          { q: 'How do I book a unit?', a: 'Open a project, choose a unit type and click Contact Developer. You will be connected directly to the sales office of the developer — Ethio Real Estates does not charge buyers any commission.' },
        ]}
      />
    </Layout>
  );
}
